import { Injectable } from '@angular/core';
import { Dataset, DatasetFacade } from '@mode/dataset/contract';
import { DatasetResult, ProducerType, ReportTypes, SwitchViewDomain } from '@mode/shared/contract-common';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { DatasetActions } from '@mode/dataset/contract';
import * as DatasetSelectors from './dataset.selectors';
import { DataViewTypes, ProducerTypes } from '@mode/shared/contract-common';
import { formatTimeAgo } from '@mode/shared/util-js';
import { Attr } from '@mode-switch/model';

@Injectable({ providedIn: 'root' })
export class DatasetService implements DatasetFacade {
  public deleted$: Observable<boolean>;
  public dataset$!: Observable<Dataset>;
  public datasetType$: Observable<ProducerTypes>;
  public datasetImportCount$!: Observable<number>;
  public datasetName$: Observable<string>;
  public isSheetDataset$: Observable<boolean>;
  public isDatasetPublished$: Observable<boolean>;
  public loadDatasetError$ = this.store.select(DatasetSelectors.loadDatasetError);
  public datasetDeletedFromCollectionToken$ = this.store.select(DatasetSelectors.selectDeleteFromCollectionToken);
  public exportedDatasetSource$ = this.store.select(DatasetSelectors.exportedDatasetSource);
  public isNotebookCellDataset$: Observable<boolean>;

  constructor(private store: Store) {
    this.deleted$ = this.store.select(DatasetSelectors.selectDeleted);
    this.dataset$ = this.store.select(DatasetSelectors.selectDataset).pipe(filter((ds): ds is Dataset => ds !== null));
    this.datasetType$ = this.store.select(DatasetSelectors.selectDatasetType);
    this.datasetName$ = this.store.select(DatasetSelectors.datasetName);
    this.datasetImportCount$ = this.dataset$.pipe(map((d) => d.details?.reportImportsCount ?? 0));
    this.isSheetDataset$ = this.datasetType$.pipe(
      map((datasetType) => this.isSheetDataset(datasetType)),
      startWith(false)
    );
    this.isDatasetPublished$ = this.store.select(DatasetSelectors.selectIsDatasetPublished);
    this.isNotebookCellDataset$ = this.datasetType$.pipe(
      map((datasetType) => this.isNotebookCellDataset(datasetType)),
      startWith(false)
    );
  }

  public loadDatasetEditRoute(datasetToken: string): void {
    this.store.dispatch(DatasetActions.loadDatasetEditRoute({ datasetToken }));
  }

  public loadDatasetViewRoute(datasetToken: string): void {
    this.store.dispatch(DatasetActions.loadDatasetViewRoute({ datasetToken }));
  }

  public runDatasetPollingCompleted({ dataView }: { dataView: SwitchViewDomain.DataView }): void {
    if (dataView.switchView.rootSourceModelType === ReportTypes.ReportType.DatasetReport) {
      this.store.dispatch(
        DatasetActions.runDatasetPollingCompleted({ datasetToken: dataView.datasetResult.datasetToken })
      );
    }
  }

  public deleteDataset(datasetToken: string): void {
    this.store.dispatch(DatasetActions.deleteDataset({ datasetToken }));
  }

  public removePhantomDataset(accountId: string, reportId: string, switchViewToken: string): void {
    this.store.dispatch(DatasetActions.removePhantomDatasetClicked({ accountId, switchViewToken, reportId }));
  }

  public googleSheetSyncCompleted({ dataView }: { dataView: SwitchViewDomain.DataView }): void {
    this.store.dispatch(DatasetActions.googleSheetSyncCompleted({ dataView }));
  }

  public openDeleteDatasetModal(token: string): void {
    this.store.dispatch(DatasetActions.openDeleteDatasetModal({ token }));
  }

  public openDeleteDatasetModalFromCollectionList(token: string): void {
    this.store.dispatch(DatasetActions.openDeleteDatasetModalFromCollectionList({ token }));
  }

  public openDeleteDatasetModalFromCollectionTile(token: string): void {
    this.store.dispatch(DatasetActions.openDeleteDatasetModalFromCollectionTile({ token }));
  }

  public openDeleteDatasetModalFromCollectionSidePanel(token: string): void {
    this.store.dispatch(DatasetActions.openDeleteDatasetModalFromCollectionSidePanel({ token }));
  }

  public deleteFromCollection(token: string): void {
    this.store.dispatch(DatasetActions.deleteFromCollection({ token }));
  }

  public loadImportedDataset(switchViewToken: string) {
    this.store.dispatch(DatasetActions.loadImportedDataset({ switchViewToken }));
  }

  public getSheetMetadata(dataset: DatasetResult): DataViewTypes.GoogleSheetMetadata | null {
    return dataset.producerResult
      ? {
          ...dataset.producerResult,
          lastSynced: formatTimeAgo(dataset.completedAt),
          lastSyncedBy: dataset.executedByEmail,
        }
      : null;
  }

  public isSheetDataset(datasetProducerType?: ProducerType) {
    return datasetProducerType === ProducerTypes.GoogleSheet;
  }

  public isNotebookCellDataset(datasetProducerType: ProducerType) {
    return datasetProducerType == ProducerTypes.NotebookCell;
  }

  public openPublishDatasetModal(switchViewToken: string): void {
    this.store.dispatch(DatasetActions.openPublishModal({ switchViewToken }));
  }

  public openRepublishDatasetModal(switchViewToken: string): void {
    this.store.dispatch(DatasetActions.openRepublishModal({ switchViewToken }));
  }

  public openUnpublishDatasetModal(switchViewToken: string): void {
    this.store.dispatch(DatasetActions.openUnpublishModal({ switchViewToken }));
  }

  public publishDataset(
    datasetToken: string,
    name: string,
    producerType: ProducerType,
    queryToken?: string,
    switchViewToken?: string
  ): void {
    this.store.dispatch(
      DatasetActions.publishDataset({ datasetToken, name, producerType, queryToken, switchViewToken })
    );
  }

  public republishDataset(datasetThoughtspotPublicationToken: string): void {
    this.store.dispatch(DatasetActions.republishDataset({ datasetThoughtspotPublicationToken }));
  }

  public unpublishDataset(
    datasetToken: string,
    producerType: ProducerType,
    switchViewToken: string,
    datasetThoughtspotPublicationToken: string
  ): void {
    this.store.dispatch(
      DatasetActions.unpublishDataset({
        datasetToken,
        producerType,
        switchViewToken,
        datasetThoughtspotPublicationToken,
      })
    );
  }

  public hasCalcFields(dataset: Dataset): boolean {
    return dataset.switchView.switchAttrs.some((attr) => attr.source === Attr.Source.Calc);
  }

  public loadExportedDatasetSource(queryToken: string): void {
    this.store.dispatch(DatasetActions.fetchExportedDatasetSource({ queryToken }));
  }
}
